/**
 * @file This file contains the ObstacleManager class, responsible for managing
 * static obstacles on the grid that affect NPC pathfinding, like walls.
 * @module ObstacleManager
 */
// 🗼🗽⛪🕌🛕🕍⛩️⛲🚏🗞️🗑️🛒
export class ObstacleManager {
    /**
     * @param {object} dependencies - The dependencies for the manager.
     * @param {import('../models/Grid.js').Grid} dependencies.grid - The game grid instance.
     * @param {import('./UIManager.js').UIManager} dependencies.ui - The UI manager instance.
     * @param {import('./DebugManager.js').DebugManager} dependencies.debugManager - The debug manager instance.
     * @param {import('./ConfigManager.js').ConfigManager} dependencies.configManager - The config manager instance.
     */
    constructor({ grid, ui, debugManager, configManager }) {
        this.grid = grid;
        this.ui = ui;
        this.debugManager = debugManager;
        this.configManager = configManager;
        /**
         * A Set of canonical string keys representing blocked edges between cells.
         * e.g., 'h-1,2' for a horizontal wall below cell (1,2).
         * @type {Set<string>}
         */
        this.obstacles = new Set();
    }

    /**
     * Initializes the manager, clearing old obstacles and generating new ones.
     */
    initManager() {
        this.obstacles.clear();
        this.ui.renderObstacles(this.obstacles);
        this.generateWalls();
        this.ui.renderObstacles(this.obstacles);
    }

    /**
     * Generates a few random walls on the grid.
     * Note: This simple generation might occasionally block paths. A more advanced
     * implementation could use maze generation algorithms to prevent this.
     */
    generateWalls() {
        const numWalls = 1//this.grid.size * 3; // The number of wall segments to generate.
        const maxWallLength = 2;

        for (let i = 0; i < numWalls; i++) {
            const isHorizontal = Math.random() < 0.5;
            const length = Math.floor(Math.random() * maxWallLength) + 1;
            
            const startCell = this.grid.getRandomPlayableCell();
            if (!startCell) continue;

            for (let j = 0; j < length; j++) {
                let r = startCell.r;
                let c = startCell.c;

                if (isHorizontal) {
                    c += j;
                    // A horizontal wall is between (r, c) and (r+1, c). Check both cells.
                    if (this.grid.isPlayable(r, c) && this.grid.isPlayable(r + 1, c)) {
                        const cellItemAbove = this.grid.get(r, c);
                        const cellItemBelow = this.grid.get(r + 1, c);

                        // Check if ANY obstacle (wall or furniture) exists on this horizontal edge.
                        const existingObstacle = Array.from(this.obstacles).some(obs => obs.endsWith(`-${r},${c}`));
                        if (existingObstacle) continue;

                        // 50% chance to place a wall, otherwise try to place street furniture.
                        if (Math.random() < 0.1) {
                            // Place a wall if both adjacent cells are clear of stationary objects.
                            if ((!cellItemAbove || !cellItemAbove.isStationary) && (!cellItemBelow || !cellItemBelow.isStationary)) { // Check both cells
                                this.obstacles.add(`h-${r},${c}`);
                            }
                        } else {
                            // Place street furniture instead of a wall. This is a visual-only change.
                            this._placeStreetFurniture(r, c);
                        }
                    }
                } else {
                    r += j;
                    // A vertical wall is between (r, c) and (r, c+1). Check both cells.
                    if (this.grid.isPlayable(r, c) && this.grid.isPlayable(r, c + 1)) {
                        const cellItemLeft = this.grid.get(r, c);
                        const cellItemRight = this.grid.get(r, c + 1);

                        // Check if ANY obstacle (wall or furniture) exists on this edge.
                        // This was the missing check that allowed vertical walls to overlap with furniture.
                        const existingVerticalObstacle = Array.from(this.obstacles).some(obs => obs.endsWith(`-${r},${c}`));
                        if (existingVerticalObstacle) continue;

                        if ((!cellItemLeft || !cellItemLeft.isStationary) && (!cellItemRight || !cellItemRight.isStationary)) { // Check both cells
                            this.obstacles.add(`v-${r},${c}`);
                        }
                    }
                }
            }
        }
        this.debugManager.log(`Generated ${this.obstacles.size} wall segments.`);
    }

    /**
     * Adds a street furniture item to the obstacles set for rendering.
     * @param {number} r - The row index of the cell above the potential wall.
     * @param {number} c - The column index of the cell.
     * @private
     */
    _placeStreetFurniture(r, c) {
        const furnitureConfigs = Object.values(this.configManager.masterItemsConfig.streetFurniture || {});
        if (furnitureConfigs.length === 0) return;

        const furnitureConfig = furnitureConfigs[Math.floor(Math.random() * furnitureConfigs.length)];
        // Add a specially formatted string to the obstacles set.
        // Format: furniture-[itemKey]-[r],[c]
        this.obstacles.add(`furniture-${furnitureConfig.key}-${r},${c}`);
    }

    /**
     * Checks if a cell contains an obstacle.
     * @param {number} r1 - Row of the first cell.
     * @param {number} c1 - Column of the first cell.
     * @param {number} r2 - Row of the second cell.
     * @param {number} c2 - Column of the second cell.
     * @returns {boolean} True if the edge between the cells is blocked.
     */
    isEdgeBlocked(r1, c1, r2, c2) {
        return false; // Temporarily disabled
        // Vertical movement
        if (c1 === c2) {
            const topR = Math.min(r1, r2);
            return this.obstacles.has(`h-${topR},${c1}`);
        }
        // Horizontal movement
        if (r1 === r2) {
            const leftC = Math.min(c1, c2);
            return this.obstacles.has(`v-${r1},${leftC}`);
        }
        return false; // Diagonal movement is not checked
    }

    getState() {
        return { obstacles: Array.from(this.obstacles) };
    }

    restoreState(state) {
        this.obstacles = new Set(state.obstacles || []);
        this.ui.renderObstacles(this.obstacles);
    }
}